import { zodResolver } from '@hookform/resolvers/zod'
import { AxiosError } from 'axios'
// import Image from 'next/image' - replaced with regular img tag
import { useParams, useRouter } from '@tanstack/react-router'
import { useTranslations } from '@/hooks/useTranslations'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormSection } from '@/components/common'
import {
  Avatar,
  AvatarImage,
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@/components/ui'
import { UserStatus } from '@/constants/user'
import { toast } from '@/components/common/Toast'
import { useConfirmSaveModal, useUnsaveWarningModal } from '@/components/common/Modals'
import { useGetRoleListNoPage } from '@/modules/role/services/role'
import { useCreateUser, useUpdateUser } from '@/modules/user/services/user'

type UserFormProps = {
  action: 'create' | 'edit'
  data?: {
    username: string
    password: string
    firstname: string
    lastname: string
    role: string
    status: UserStatus
  }
  id?: string
}

const getInitData = () => ({
  username: '',
  password: '',
  firstname: '',
  lastname: '',
  role: '',
  status: UserStatus.ACTIVE,
})

const defaultImage = 'https://placehold.co/64'

export const UserForm = ({ action, data, id }: UserFormProps) => {
  const isCreate = action === 'create'
  const isEdit = action === 'edit'

  const cancelButtonClick = useRef(false)
  const { ConfirmSaveModal, showConfirmSaveModal } = useConfirmSaveModal()
  const { UnsaveWarningModal, showUnsaveWarningModal } = useUnsaveWarningModal()

  const t = useTranslations('admin.user_management.form')
  const params = useParams({ strict: false })
  const router = useRouter()

  const { data: roleList } = useGetRoleListNoPage()

  const $apiCreateUser = useCreateUser()
  const $apiUpdateUser = useUpdateUser()

  const formSchema = z.object({
    username: z
      .string().optional()
      .refine((val) => isCreate ? val?.length : true, t('user_information.username.validation.empty')),
    password: z.string().optional()
      .refine((val) => isCreate ? val?.length : true, t('user_information.password.validation.empty')),
    firstname: z.string().min(1, t('user_information.firstname.validation.empty')),
    lastname: z.string().min(1, t('user_information.lastname.validation.empty')),
    role: z.string().min(1, 'Please select role.'),
    status: z.nativeEnum(UserStatus),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: isEdit ? data : getInitData(),
  })

  useEffect(() => {
    if (isEdit) {
      form.reset(data)
    }
  }, [isEdit, data])


  const userStatusOptions = useMemo(
    () => [
      {
        label: "เปิดใช้งาน",
        value: UserStatus.ACTIVE,
      },
      {
        label: "ปิดใช้งาน",
        value: UserStatus.INACTIVE,
      },
    ],
    [t],
  )

  const isLoading = false

  const createUser = async () => {
    const data = form.getValues()

    try {
      const payload = {
        firstname: data.firstname,
        lastname: data.lastname,
        username: data.username ?? '',
        password: data.password ?? '',
        roleId: data.role,
        status: data.status,
      }

      await $apiCreateUser.mutateAsync(payload)
      toast.toastSuccess('สร้างผู้ใช้งานสำเร็จ')
      router.navigate({ to: '/admin/user', replace: true })
    } catch (e) {
      if (e instanceof Error) {
        toast.toastFailed(e.message)
      }
    }
  }

  const updateUser = async () => {
    const data = form.getValues()
    console.log(data)
    try {
      const payload = {
        firstname: data.firstname,
        lastname: data.lastname,
        username: data.username ?? '',
        password: data.password ?? '',
        roleId: data.role,
        status: data.status,
        id: id ?? '',
      }
      await $apiUpdateUser.mutateAsync(payload)
      toast.toastSuccess('อัพเดตผู้ใช้งานสำเร็จ')
      router.navigate({ to: '/admin/user', replace: true })
    } catch (e) {
      if (e instanceof AxiosError) {
        toast.toastFailed(e.response?.data.message ?? e.message)
      } else if (e instanceof Error) {
        toast.toastFailed(e.message)
      }
    }
  }

  console.log(form.formState.errors)

  const handleFormSubmit = async () => {
    switch (action) {
      case 'create':
        await createUser()
        break

      case 'edit':
        updateUser()
        break
    }
  }


  useEffect(() => {
    const onBackButtonEvent = (e: Event) => {
      e.preventDefault()

      if (!cancelButtonClick.current) {
        if (window.confirm("คุณต้องการออกจากหน้านี้หรือไม่? การเปลี่ยนแปลงที่ยังไม่ได้บันทึกจะสูญหาย")) {
          cancelButtonClick.current = true
          setTimeout(() => {
            router.navigate({ to: '/admin/user', replace: true })
          }, 250)
        } else {
          cancelButtonClick.current = false
        }
      }
    }

    window.history.pushState(null, '', window.location.pathname)
    window.addEventListener('popstate', onBackButtonEvent)

    return () => {
      window.removeEventListener('popstate', onBackButtonEvent)
    }
  }, [t])

  const roleOptions = useMemo(() => {
    return roleList?.data.map((role) => ({
      label: role.name,
      value: role.id,
    })) ?? []
  }, [roleList])

  return (
    <Form {...form}>
      <form
        className="space-y-8"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <FormSection title={t('user_information.title')}>
          <div className="flex flex-col space-y-8">
            {isCreate && <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('user_information.username.label')}</FormLabel>
                    {/* <FormDescription>{t('user_information.email.description')}</FormDescription> */}
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('user_information.username.placeholder')}
                        disabled={isLoading || isEdit}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />}
            {isCreate && <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('user_information.password.label')}</FormLabel>
                    {/* <FormDescription>{t('user_information.email.description')}</FormDescription> */}
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('user_information.password.placeholder')}
                        disabled={isLoading || isEdit}
                        autoComplete="off"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />}
            <FormField
              control={form.control}
              name="firstname"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('user_information.firstname.label')}</FormLabel>
                    {/* <FormDescription>{t('user_information.email.description')}</FormDescription> */}
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('user_information.firstname.placeholder')}
                        disabled={isLoading}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastname"
              render={({ field }) => (
                <FormItem className="flex gap-4 flex-col lg:flex-row">
                  <div className="w-60 space-y-2">
                    <FormLabel required>{t('user_information.lastname.label')}</FormLabel>
                    {/* <FormDescription>{t('user_information.email.description')}</FormDescription> */}
                  </div>
                  <div className="flex-1">
                    <FormControl>
                      <Input
                        className="lg:max-w-96"
                        placeholder={t('user_information.lastname.placeholder')}
                        disabled={isLoading}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage className="px-3 mt-2" />
                  </div>
                </FormItem>
              )}
            />
            {isEdit ? (
              <>
                <Separator />
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="flex gap-4 flex-col lg:flex-row">
                      <div className="w-60 space-y-2">
                        <FormLabel>{t('user_information.status.label')}</FormLabel>
                        {/* <FormDescription>{t('user_information.status.description')}</FormDescription> */}
                      </div>
                      <div className="flex-1">
                        <FormControl>
                          <RadioGroup
                            className="flex flex-row items-center gap-4"
                            onValueChange={field.onChange}
                            value={field.value}
                            disabled={isLoading}
                          >
                            {userStatusOptions.map((option) => (
                              <FormItem
                                key={option.value}
                                className="flex items-center gap-x-2 space-y-0"
                              >
                                <FormControl>
                                  <RadioGroupItem value={option.value} />
                                </FormControl>
                                <FormLabel
                                  radioGroup={field.name}
                                  className="font-normal"
                                >
                                  {option.label}
                                </FormLabel>
                              </FormItem>
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </FormItem>
                  )}
                ></FormField>
              </>
            ) : undefined}
          </div>
        </FormSection>
        <FormSection title={t('user_permissions.title')}>
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className="flex gap-4 flex-col lg:flex-row">
                <div className="w-60 space-y-2">
                  <FormLabel required>{t('user_permissions.role.label')}</FormLabel>
                  {/* <FormDescription>{t('user_information.status.description')}</FormDescription> */}
                </div>
                <div className="flex-1">
                  <div className="lg:max-w-96">
                    <Select
                      value={field.value}
                      disabled={isLoading || form.watch('status') === UserStatus.INACTIVE}
                      onValueChange={(value) => {
                        if (value.length) field.onChange(value)
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('user_permissions.role.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roleOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <FormMessage className="px-3 mt-2" />
                </div>
              </FormItem>
            )}
          />
        </FormSection>
        <div className="flex justify-end mobile:justify-center mobile:w-full gap-4">
          <Button
            type="button"
            variant="ghost"
            disabled={isLoading}
            className="text-t-secondary mobile:w-full"
            onClick={() => showUnsaveWarningModal()}
          >
            {t('button.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="mobile:w-full"
          >
            {t('button.save')}
          </Button>
        </div>
        <UnsaveWarningModal onDiscard={() => router.navigate({ to: '/admin/user', replace: true })} />
      </form>
    </Form>
  )
}
